// ── TradeModal — V2 order entry overlay ──────────────────────────
// Matches V1's polish: stepper inputs, summary box, expiry options,
// team badges. Opens on odds cell click in GameTable or depth rows.

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { X, Loader2, Minus, Plus } from 'lucide-react';
import { useAccountStore } from '../../stores/accountStore';
import type { GameRow, VenueBook } from './types';
import { bestPriceCentsForOrderSide, outcomeSideLabel } from './tradeModalBook';

export interface TradeModalData {
  game: GameRow;
  teamSide: 'away' | 'home';
  venue: 'kalshi' | 'polymarket';
  /** Limit price in cents for the opened order side (YES or NO leg). */
  prefillPrice?: number;
  /** Default YES/NO; depth rows use NO (butterfly levels are NO-book prices). */
  initialOrderSide?: 'yes' | 'no';
}

interface TradeModalProps {
  data: TradeModalData;
  onClose: () => void;
}

type ExpiryMode = 'preset' | 'custom' | 'gtc';

/** Build an ISO timestamp from date string + time string (PT) */
function buildExpiryIso(dateStr: string, timeStr: string): string | null {
  // dateStr: "MM/DD/YYYY", timeStr: "h:mm AM/PM"
  const [month, day, year] = dateStr.split('/').map(Number);
  if (!month || !day || !year) return null;

  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toUpperCase();

  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;

  // Build as PT (UTC-7) — good enough for dev, production should use proper tz
  const dt = new Date(Date.UTC(year, month - 1, day, hours + 7, minutes));
  return dt.toISOString();
}

/** Format date for preset display */
function todayDateStr(): string {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

/** Round up to nearest 10-min slot in PT */
function defaultPresetTime(): string {
  const now = new Date();
  // Rough PT offset (UTC-7)
  const ptHours = (now.getUTCHours() - 7 + 24) % 24;
  const ptMinutes = Math.ceil(now.getUTCMinutes() / 10) * 10;
  const h = ptMinutes >= 60 ? ptHours + 1 : ptHours;
  const m = ptMinutes >= 60 ? 0 : ptMinutes;
  const hour12 = h % 12 || 12;
  const period = h >= 12 ? 'PM' : 'AM';
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

function StepperInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  placeholder,
  suffix,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  suffix?: string;
  autoFocus?: boolean;
}) {
  const numVal = parseInt(value, 10);
  const canDec = !isNaN(numVal) && (min === undefined || numVal - step >= min);
  const canInc = !isNaN(numVal) && (max === undefined || numVal + step <= max);

  return (
    <div className="flex items-center rounded-lg border border-gray-700 bg-gray-800">
      <button
        type="button"
        onClick={() => canDec && onChange(String(numVal - step))}
        disabled={!canDec}
        className="flex h-10 w-10 items-center justify-center text-gray-400 transition-colors hover:text-white disabled:opacity-30"
      >
        <Minus size={14} />
      </button>
      <div className="relative flex-1">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="w-full bg-transparent py-2 text-center font-mono text-lg text-white placeholder-gray-600 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        {suffix && value && (
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-500">{suffix}</span>
        )}
      </div>
      <button
        type="button"
        onClick={() => {
          if (value === '' || isNaN(numVal)) {
            onChange(String(min ?? step));
          } else if (canInc) {
            onChange(String(numVal + step));
          }
        }}
        disabled={!isNaN(numVal) && !canInc}
        className="flex h-10 w-10 items-center justify-center text-gray-400 transition-colors hover:text-white disabled:opacity-30"
      >
        <Plus size={14} />
      </button>
    </div>
  );
}

// ── Time preset options ───────────────────────────────────────────

const TIME_OPTIONS = (() => {
  const opts: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 10) {
      const hour12 = h % 12 || 12;
      const period = h >= 12 ? 'PM' : 'AM';
      opts.push(`${hour12}:${String(m).padStart(2, '0')} ${period}`);
    }
  }
  return opts;
})();

export function TradeModal({ data, onClose }: TradeModalProps) {
  const status = useAccountStore((s) => s.status);
  const placeOrder = useAccountStore((s) => s.placeOrder);

  const { game, teamSide, venue } = data;
  const teamCode = teamSide === 'away' ? game.awayCode : game.homeCode;
  const venueLabel = venue === 'kalshi' ? 'KALSHI' : 'POLY';

  // Get the book for the selected team + venue
  const book: VenueBook | undefined = teamSide === 'away'
    ? (venue === 'kalshi' ? game.kalshi?.away : game.polymarket?.away)
    : (venue === 'kalshi' ? game.kalshi?.home : game.polymarket?.home);

  const ticker = book?.marketTicker ?? '';
  const initialSide = data.initialOrderSide ?? 'yes';
  const bestForInitial =
    data.prefillPrice ?? bestPriceCentsForOrderSide(book, initialSide);
  const bestYesHint = bestPriceCentsForOrderSide(book, 'yes');
  const bestNoHint = bestPriceCentsForOrderSide(book, 'no');

  // ── Form state ──────────────────────────────────────────────────
  const [orderSide, setOrderSide] = useState<'yes' | 'no'>(initialSide);
  const [price, setPrice] = useState(
    () => (bestForInitial !== undefined ? String(bestForInitial) : ''),
  );
  const [qty, setQty] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const autoCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Expiry state ────────────────────────────────────────────────
  const [expiryMode, setExpiryMode] = useState<ExpiryMode>('gtc');
  const [expiryDate, setExpiryDate] = useState(todayDateStr);
  const [expiryTime, setExpiryTime] = useState(defaultPresetTime);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Clean up auto-close timer on unmount
  useEffect(() => {
    return () => {
      if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
    };
  }, []);

  const applyOrderSide = useCallback(
    (next: 'yes' | 'no') => {
      setOrderSide(next);
      setResult(null);
      const hint = bestPriceCentsForOrderSide(book, next);
      if (hint !== undefined) setPrice(String(hint));
    },
    [book],
  );

  // ── Computed values ─────────────────────────────────────────────
  const priceCents = parseInt(price, 10) || 0;
  const count = parseInt(qty, 10) || 0;
  const totalCost = (priceCents * count) / 100;
  const maxPayout = count;
  const maxProfit = maxPayout - totalCost;

  const expiryIso = useMemo(() => {
    if (expiryMode === 'gtc') return undefined;
    return buildExpiryIso(expiryDate, expiryTime) ?? undefined;
  }, [expiryMode, expiryDate, expiryTime]);

  const handleSubmit = useCallback(async () => {
    const p = parseInt(price, 10);
    const c = parseInt(qty, 10);

    if (!ticker) { setResult({ ok: false, msg: 'No market ticker found' }); return; }
    if (isNaN(p) || p < 1 || p > 99) { setResult({ ok: false, msg: 'Price must be 1-99\u00A2' }); return; }
    if (isNaN(c) || c < 1) { setResult({ ok: false, msg: 'Qty must be at least 1' }); return; }

    setSubmitting(true);
    setResult(null);
    try {
      await placeOrder({
        ticker,
        side: orderSide,
        action: 'buy',
        type: 'limit',
        count: c,
        price: p,
        ...(expiryIso ? { expirationTime: expiryIso } : {}),
      });
      setResult({ ok: true, msg: `Order placed: ${c} ${orderSide.toUpperCase()} @ ${p}\u00A2` });
      setQty('');
      autoCloseRef.current = setTimeout(() => onClose(), 1500);
    } catch (err) {
      setResult({ ok: false, msg: err instanceof Error ? err.message : 'Order failed' });
    } finally {
      setSubmitting(false);
    }
  }, [ticker, orderSide, price, qty, placeOrder, expiryIso, onClose]);

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      {/* Modal */}
      <div
        className="w-[420px] rounded-lg border border-gray-700 bg-gray-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-800 px-5 py-4">
          <h2 className="text-base font-bold tracking-wide text-white">PLACE ORDER</h2>
          <button onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-800 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-5 py-4">
          {status !== 'connected' ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Connect your Kalshi account in Settings to trade
            </p>
          ) : !ticker ? (
            <p className="py-8 text-center text-sm text-gray-500">
              No {venue === 'kalshi' ? 'Kalshi' : 'Polymarket'} market available
            </p>
          ) : (
            <>
              {/* Team + Badges */}
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-white">{teamCode}</span>
                  <span className="rounded bg-green-600/80 px-1.5 py-0.5 text-[10px] font-bold text-white">WIN</span>
                  <span className="rounded bg-blue-600/80 px-1.5 py-0.5 text-[10px] font-bold text-white">{venueLabel}</span>
                  <span className="rounded bg-purple-600/80 px-1.5 py-0.5 text-[10px] font-bold text-white">MAKER</span>
                </div>
                <p className="mt-1 font-mono text-xs text-gray-500">
                  {game.awayCode} @ {game.homeCode} &middot; {game.date} &middot; {ticker.length > 40 ? `${ticker.slice(0, 20)}...${ticker.slice(-18)}` : ticker}
                </p>
              </div>

              {/* Side */}
              <div className="rounded-lg border border-gray-700 px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Side</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => applyOrderSide('yes')}
                      className={`rounded px-3 py-1 text-xs font-bold transition-colors ${
                        orderSide === 'yes'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-800 text-gray-500 hover:text-gray-300'
                      }`}
                    >
                      YES
                    </button>
                    <button
                      type="button"
                      onClick={() => applyOrderSide('no')}
                      className={`rounded px-3 py-1 text-xs font-bold transition-colors ${
                        orderSide === 'no'
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-800 text-gray-500 hover:text-gray-300'
                      }`}
                    >
                      NO
                    </button>
                    <span className="text-sm text-gray-300">
                      {outcomeSideLabel(game, teamSide, orderSide)}
                    </span>
                  </div>
                </div>

                {/* Limit Price */}
                <div className="mt-3">
                  <label className="mb-1.5 block text-xs text-gray-500">Limit Price</label>
                  <StepperInput
                    value={price}
                    onChange={(v) => { setPrice(v); setResult(null); }}
                    min={1}
                    max={99}
                    placeholder={
                      (orderSide === 'yes' ? bestYesHint : bestNoHint) !== undefined
                        ? String(orderSide === 'yes' ? bestYesHint : bestNoHint)
                        : '50'
                    }
                    suffix={'\u00A2'}
                    autoFocus
                  />
                </div>
              </div>

              {/* Contracts */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs text-gray-500">Contracts</label>
                </div>
                <StepperInput
                  value={qty}
                  onChange={(v) => { setQty(v); setResult(null); }}
                  min={1}
                  step={1}
                  placeholder="0"
                />
                {count > 0 && priceCents > 0 && (
                  <p className="mt-1.5 text-center text-xs text-gray-500">
                    ${totalCost.toFixed(2)} total cost
                  </p>
                )}
              </div>

              {/* Summary box */}
              {count > 0 && priceCents > 0 && (
                <div className="rounded-lg border border-gray-700 px-4 py-3 space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Total Cost</span>
                    <span className="font-mono font-medium text-white">${totalCost.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Max Payout</span>
                    <span className="font-mono font-medium text-white">${maxPayout.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Max Profit</span>
                    <span className={`font-mono font-medium ${maxProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {maxProfit >= 0 ? '+' : ''}{maxProfit.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {/* Expiry */}
              <div>
                <label className="mb-1.5 block text-xs text-gray-500">Expiry</label>
                <div className="mb-2 flex gap-1.5">
                  {(['preset', 'custom', 'gtc'] as ExpiryMode[]).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setExpiryMode(mode)}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        expiryMode === mode
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:text-gray-300'
                      }`}
                    >
                      {mode === 'preset' ? 'Preset' : mode === 'custom' ? 'Custom' : 'GTC'}
                    </button>
                  ))}
                </div>
                {expiryMode !== 'gtc' && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      placeholder="MM/DD/YYYY"
                      className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 font-mono text-sm text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none"
                    />
                    <select
                      value={expiryTime}
                      onChange={(e) => setExpiryTime(e.target.value)}
                      className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 font-mono text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                    >
                      {TIME_OPTIONS.map((t) => (
                        <option key={t} value={t}>{t} PT</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Result message */}
              {result && (
                <div className={`rounded-lg px-4 py-2.5 text-sm ${result.ok ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                  {result.msg}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {status === 'connected' && ticker && (
          <div className="flex items-center justify-end gap-3 border-t border-gray-800 px-5 py-3">
            <button
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm text-gray-400 transition-colors hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !price || !qty}
              className="flex items-center gap-2 rounded-md bg-blue-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-50"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? 'Placing...' : 'Place Order'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
